import { Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import {
  OrdersCacheEntry,
  tableOrders,
  tableOrdersInWork,
  tableOrdersCompleted
} from '../db-description';

/**
 * Очистка локальных данных пользователя в IndexedDB при выходе и смене субъекта.
 *
 * <p>Назначение: не оставлять на устройстве данные предыдущего субъекта (кэши списков заказов с
 * персональными данными), когда в приложение входит другой пользователь или текущий выходит.
 *
 * <p><b>Что НЕ трогаем намеренно:</b> очереди несинхронизированных действий — {@code SyncActionsTable}
 * (подписи расписок экспедитора и завершение заявок), {@code BackupActionsTable} (резервные копии
 * заказов), {@code DocumentsForUpload} (документы на загрузку). В них лежат ЮРИДИЧЕСКИ значимые
 * несинхронизированные документы; их потеря недопустима. Сохранность важнее приватности, а изоляция
 * данных по e-mail не даёт другому субъекту их прочитать — очереди дождутся следующего входа своего
 * владельца и синхронизируются.
 *
 * <p>{@code ordersInEditStatus} (рабочий слой резервных копий с данными РТС) тоже не трогаем по двум
 * причинам: это часть механизма резервных копий (терять нельзя) и его в принципе НЕЛЬЗЯ вычистить
 * по субъекту — ключ хранилища {@code Id} (номер заказа), индекса по {@code user} нет. Остаток
 * персональных данных прежнего субъекта здесь принимается осознанно (приоритет сохранности).
 *
 * <p>Все операции — «выстрелил и забыл»: любая ошибка перехватывается и логируется, поток никогда не
 * завершается ошибкой, чтобы очистка не заблокировала вход или выход.
 */
@Injectable({ providedIn: 'root' })
export class LocalDataCleanupService {
  /** Кэши списков заказов: перезагружаемы с сервера, ключ [pageId, filter, user], индекс 'user'. */
  private static readonly CACHE_STORES: string[] = [
    tableOrders,
    tableOrdersInWork,
    tableOrdersCompleted
  ];

  constructor(private readonly dbService: NgxIndexedDBService) {}

  /**
   * Удаляет кэши списков заказов указанного субъекта. Очереди несинхронизированных действий не
   * затрагиваются (см. описание класса). Никогда не завершается ошибкой.
   *
   * @param email e-mail субъекта; {@code null}/пусто — чистить нечего, возвращает пустой поток.
   */
  public clearSubjectData(email: string | null): Observable<void> {
    if (!email) {
      return of(undefined);
    }

    const tasks: Observable<void>[] = LocalDataCleanupService.CACHE_STORES.map(
      store => this.clearCacheStoreForUser(store, email)
    );

    return forkJoin(tasks).pipe(
      map(() => undefined),
      catchError(error => {
        Sentry.captureException(error);
        return of(undefined);
      })
    );
  }

  /** Удаляет записи кэша заказов конкретного субъекта по составному ключу [pageId, filter, user]. */
  private clearCacheStoreForUser(store: string, email: string): Observable<void> {
    return this.dbService
      .getAllByIndex<OrdersCacheEntry>(store, 'user', IDBKeyRange.only(email))
      .pipe(
        switchMap(records => {
          if (!records.length) {
            return of(undefined);
          }
          const deletions = records.map(record =>
            this.dbService
              .deleteByKey(store, [record.pageId, record.filter, record.user])
              .pipe(catchError(() => of(undefined)))
          );
          return forkJoin(deletions).pipe(map(() => undefined));
        }),
        catchError(error => {
          Sentry.captureException(error);
          return of(undefined);
        })
      );
  }
}
